import * as React from "react";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkSecondaryButton } from "@egds/react-core/button";
import { UitkProgressBar } from "@egds/react-core/progress-bar";
import { UitkCard, UitkCardContentSection } from "@egds/react-core/cards";
import { UitkLink } from "@egds/react-core/link";
import { UitkSpacing } from "@egds/react-core/spacing";
import { UitkLayoutGrid, UitkLayoutGridItem } from "@egds/react-core/layout-grid";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "@egds/react-core/layout-flex";
import { UitkHeading, UitkText, UitkParagraph } from "@egds/react-core/text";

import { FlexClickTracker } from "src/components/utility/analytics/FlexClickTracker/FlexClickTracker";

import { SampleReviewProps, DestinationSampleReviewsProps } from "../typings";

const MAX_NUM_REVIEWS_DISPLAY = 6;

const DestinationSampleReviews = (props: DestinationSampleReviewsProps) => {
  const {
    title,
    nameForTracking,
    reviews,
    destinationName,
    totalReviews,
    totalRatings,
    countPerRating,
    avgRating,
    toggleAllDestinationReviews,
    hideDate,
  } = props;
  const { formatText } = useLocalization();
  const totalReviewsLocalized = totalReviews.toLocaleString();
  const sampleReviews = reviews.filter((review) => review.rating).slice(0, MAX_NUM_REVIEWS_DISPLAY);

  const ratingDescriptor =
    avgRating >= 4 ? formatText("destinationReviews.topDestination") : formatText("destinationReviews.guestRating");

  const ButtonTracked = FlexClickTracker(UitkSecondaryButton);
  const LinkTracked = FlexClickTracker(UitkLink);

  delete countPerRating["0"];
  const ratingBars = Object.values(countPerRating)
    .map((rating, index) => {
      const percentage = (rating * 100) / totalRatings;
      const ratingScore = index + 1;

      return (
        <UitkSpacing padding={{ blockend: "two" }} key={index}>
          <div>
            <UitkProgressBar
              percent={percentage}
              progressDescription={`${rating}`}
              title={`${ratingScore} - ${formatText(`destinationReviews.descriptor.${ratingScore}`)}`}
            />
          </div>
        </UitkSpacing>
      );
    })
    .reverse();

  return (
    <UitkSpacing padding="six">
      <div className="DestinationSampleReviews">
        <div itemScope itemType="http://schema.org/AggregateRating" itemProp="aggregateRating">
          <meta itemProp="reviewCount" content={String(totalReviews)} />
          <meta itemProp="ratingValue" content={String(avgRating)} />
        </div>

        <UitkSpacing margin={{ blockend: "four" }}>
          <UitkHeading tag="h2" size={4}>
            {title || formatText("destinationReviews.cityReviews", destinationName)}
          </UitkHeading>
        </UitkSpacing>

        <UitkLayoutGrid space={{ column: "six" }} columns={{ small: ["1fr"], large: ["1fr", "2fr"] }}>
          <UitkLayoutGridItem>
            <div>
              <UitkLayoutFlex>
                <UitkLayoutFlexItem>
                  <UitkText size={900}>{avgRating}</UitkText>
                </UitkLayoutFlexItem>
                <UitkLayoutFlexItem>
                  <UitkSpacing padding={{ inlinestart: "three", blockstart: "one" }}>
                    <UitkText size={500} weight="bold">
                      <>
                        {ratingDescriptor}
                        <div>
                          <LinkTracked
                            rfrr="Detail.OpenReviews"
                            onClick={toggleAllDestinationReviews}
                            moduleName={nameForTracking}
                            linkName="RFRR Action Event"
                          >
                            <button type="button">
                              <UitkText size={300} inline>
                                {formatText("destinationReviews.numberOfReviews", totalReviews, totalReviewsLocalized)}
                              </UitkText>
                            </button>
                          </LinkTracked>
                        </div>
                      </>
                    </UitkText>
                  </UitkSpacing>
                </UitkLayoutFlexItem>
              </UitkLayoutFlex>
              <UitkSpacing padding={{ blockstart: "three" }}>
                <div>{ratingBars}</div>
              </UitkSpacing>
            </div>
          </UitkLayoutGridItem>
          <UitkLayoutGridItem>
            <div className="DestinationUserReviewsContainer">
              {sampleReviews.map((review, i: number) => {
                const isLast = i === sampleReviews.length - 1;

                return (
                  <SampleReview
                    destinationReview={review}
                    showBorder={!isLast}
                    key={review.postStayReviewId}
                    hideDate={hideDate}
                  />
                );
              })}
              <UitkSpacing margin={{ blockstart: "three" }}>
                <ButtonTracked
                  onClick={toggleAllDestinationReviews}
                  rfrr="Detail.OpenReviews"
                  moduleName={nameForTracking}
                  linkName="RFRR Action Event"
                >
                  {formatText(
                    "destinationReviews.seeAllCityReviews",
                    totalReviews,
                    totalReviewsLocalized,
                    destinationName
                  )}
                </ButtonTracked>
              </UitkSpacing>
            </div>
          </UitkLayoutGridItem>
        </UitkLayoutGrid>
      </div>
    </UitkSpacing>
  );
};

const SampleReview = (props: SampleReviewProps) => {
  const { review, rating, createTime, reviewerDisplayName } = props.destinationReview;
  const date = new Date(createTime);
  const { hideDate } = props;
  const { formatText, formatDateString } = useLocalization();

  return (
    <UitkCard>
      <UitkCardContentSection border="bottom">
        <UitkSpacing padding={{ block: "six" }}>
          <article>
            {rating && (
              <UitkSpacing padding={{ blockend: "three" }}>
                <UitkHeading tag="h3" size={5}>
                  {`${rating}/5 - ${formatText(`destinationReviews.descriptor.${rating}`)}`}
                </UitkHeading>
              </UitkSpacing>
            )}
            <UitkSpacing padding={{ blockend: "two" }}>
              <div>
                <UitkText weight="medium" size={300} theme="emphasis">
                  {reviewerDisplayName || formatText("destinationReviews.verified.traveler")}
                </UitkText>
                <UitkText size={300} inline>
                  {date && !hideDate && formatDateString(date, { skeleton: "yMMMd" })}
                </UitkText>
              </div>
            </UitkSpacing>

            <div className="review-text">
              <UitkParagraph>{review}</UitkParagraph>
            </div>
          </article>
        </UitkSpacing>
      </UitkCardContentSection>
    </UitkCard>
  );
};

export default DestinationSampleReviews;
